import { IconTile } from "@cmp-src/data-display/IconTile";
import { UserAvatar } from "@cmp-src/data-display/UserAvatar/UserAvatar";
import { Button } from "@gettactic/components/src/primitives/button";
import { getCategoryAssignedUsers } from "@gettactic/helpers/src/workplace-requests/categoryAssignees";
import { formatDistanceToNow } from "date-fns";
import { Pencil } from "lucide-react";
import { useMemo, useState } from "react";
import { SafeReserveEditModal } from "@/features/reserve/components/SafeReserveEditModal";
import { ParentRequestCard } from "@/features/workplace-requests/components/detail-modal/ParentRequestCard";
import { useWorkplaceRequestCategories } from "@/features/workplace-requests/hooks/useWorkplaceRequestCategories";
import {
  getPriorityMeta,
  PRIORITY_OPTIONS
} from "@/features/workplace-requests/lib/priority";
import { DEFAULT_CATEGORY_COLOR } from "../../categories/components/ColorSwatchSelect";
import { parseIconValue } from "../../requests/icon-value";
import { DetailsSideItem } from "./DetailsSideItem";
import { RequestSelect } from "./RequestSelect";
import { ScheduleInfoCard, useScheduleById } from "./ScheduleInfoCard";
import { SubStatusSelect } from "./SubStatusSelect";
import { SidebarProps } from "./types";
import { classes } from "./utils";

// Priority is stored as 1|2|3 on the request; 0/unset renders as "None".
const PriorityTrigger = ({ value }: { value: string }) => {
  const meta = getPriorityMeta(Number(value));
  return (
    <span className="flex items-center gap-2 font-inter font-normal text-sm">
      <span
        className={`h-2 w-2 rounded-full ${meta ? meta.dotClass : "bg-neutral-300"}`}
      />
      {meta ? meta.label : "None"}
    </span>
  );
};

export const Sidebar = ({
  request,
  workplaceRequestTypes,
  pendingChanges,
  onOwnerChange,
  onStatusChange,
  onTypeChange,
  onPriorityChange,
  onOpenLinkedRequest,
  schedule: passedSchedule
}: SidebarProps) => {
  const selectedType = workplaceRequestTypes.find(
    (t) => t.id === pendingChanges.type
  );
  const parentType = workplaceRequestTypes.find(
    (t) => t.id === request.parent?.type?.id
  );

  // Editability is resolved by the backend per-request (it accounts for
  // category membership, not just org-wide admin permissions) — read it off
  // the request instead of re-deriving it from the viewer's role.
  const canEdit = Boolean(request.capabilities?.can_edit);
  const canEditOwner = Boolean(request.capabilities?.can_edit_owner);

  // Tint the type tile to match its category, as the queue table and header do.
  // Also doubles as the assignee lookup below, so it needs the same explicit
  // `limit` as the controller's fetch — a category outside the backend's
  // default page size would otherwise silently fail to resolve.
  const categories = useWorkplaceRequestCategories({ limit: 100 });
  const categoryColorById = useMemo(() => {
    const map = new Map<string, string>();
    for (const c of categories.allElements) {
      if (c.color) {
        map.set(c.id, c.color);
      }
    }
    return map;
  }, [categories.allElements]);
  const selectedTypeColor =
    categoryColorById.get(selectedType?.category_id ?? "") ||
    DEFAULT_CATEGORY_COLOR;

  // The assignee list: `type.assigned_users` is an admin-only shim (present
  // only via the admin ticket-types endpoint), so a category member without
  // org-wide scheduling permissions never gets it. Read the category's own
  // membership instead — office members when set, otherwise the global
  // default — which the viewer always has, regardless of admin status.
  const selectedCategory = categories.allElements.find(
    (c) => c.id === selectedType?.category_id
  );
  const assignedUsers = getCategoryAssignedUsers(
    selectedCategory,
    request.office_id
  );

  // Always keep this live: some openers pass a schedule they already had in
  // memory as a fast-path (avoids a redundant fetch on first paint), but that
  // prop is static — a reservation edit invalidates ["schedule", id], and a
  // disabled query never picks the invalidation up, so the card would keep
  // showing pre-edit times until the modal was closed and reopened. Prefer
  // the fetched (and therefore refetch-on-invalidate) copy once it resolves.
  const { data: fetchedSchedule } = useScheduleById(
    request?.schedule_id || null
  );
  const schedule = fetchedSchedule || passedSchedule;
  const [isEditingSchedule, setIsEditingSchedule] = useState(false);

  return (
    <div className="space-y-6">
      {/* Status */}
      <DetailsSideItem
        label={
          <label htmlFor="status-select" className={classes.label}>
            Status
          </label>
        }
        value={
          canEdit ? (
            <div className="w-full">
              <SubStatusSelect
                request={request}
                typeStatuses={selectedType?.statuses ?? []}
                onStatusChange={onStatusChange}
              />
            </div>
          ) : (
            <div className="flex items-center space-x-1">
              <span className="font-normal text-neutral-900 text-sm">
                {request.sub_status?.name ?? pendingChanges.status}
              </span>
            </div>
          )
        }
      />

      {/* Priority */}
      <DetailsSideItem
        label={
          <label htmlFor="priority-select" className={classes.label}>
            Priority
          </label>
        }
        value={
          canEdit ? (
            <RequestSelect
              value={pendingChanges.priority}
              onValueChange={onPriorityChange}
              className="focus:ring-0"
              triggerContent={
                <PriorityTrigger value={pendingChanges.priority} />
              }
              options={PRIORITY_OPTIONS.map((p) => ({
                id: String(p.value),
                name: p.label,
                dotClass: p.dotClass
              }))}
            />
          ) : (
            <PriorityTrigger value={pendingChanges.priority} />
          )
        }
      />

      {/* Assignee (owner) */}
      <DetailsSideItem
        label="Assignee"
        value={
          canEditOwner ? (
            <RequestSelect
              value={pendingChanges.owner}
              onValueChange={onOwnerChange}
              className="focus:ring-0"
              triggerContent={
                <div className="flex w-full min-w-0 items-center space-x-2">
                  <UserAvatar
                    user={{
                      name: request.owner_user?.name || "?",
                      email: request.owner_user?.email || "",
                      profile_photo_url:
                        request.owner_user?.profile_photo_url || ""
                    }}
                    size="xs"
                    className="flex-shrink-0"
                  />
                  <span className="truncate font-inter font-normal text-sm">
                    {request.owner_user?.name || "No owner"}
                  </span>
                </div>
              }
              options={[
                {
                  id: "no-owner",
                  name: "No owner",
                  profile_photo_url: undefined
                },
                ...assignedUsers.map(
                  (user: {
                    id: string;
                    name?: string;
                    email?: string;
                    profile_photo_url?: string;
                  }) => ({
                    id: user.id,
                    name: user.name || "?",
                    email: user.email,
                    profile_photo_url: user.profile_photo_url || ""
                  })
                )
              ]}
            />
          ) : (
            <div className="flex items-center space-x-2">
              <UserAvatar
                user={{
                  name: request.owner_user?.name || "",
                  email: request.owner_user?.email || "",
                  profile_photo_url: request.owner_user?.profile_photo_url || ""
                }}
                size="xs"
                className="flex-shrink-0"
              />
              <span className="font-inter font-normal text-neutral-900 text-sm">
                {request.owner_user?.name || "No owner"}
              </span>
            </div>
          )
        }
      />

      {/* Workplace Request Type */}
      <DetailsSideItem
        label={
          <label htmlFor="type-select" className={classes.label}>
            Workplace Request Type
          </label>
        }
        value={
          <RequestSelect
            value={pendingChanges.type}
            onValueChange={onTypeChange}
            disabled={!canEdit}
            className="px-4 focus:ring-0"
            triggerContent={
              <span className="flex min-w-0 items-center gap-2 font-inter font-normal text-sm">
                <IconTile
                  color={selectedTypeColor}
                  icon={parseIconValue(selectedType?.icon) ?? null}
                  size="sm"
                  className="h-5 w-5 rounded-[6px]"
                />
                <span className="truncate">
                  {selectedType?.name || "Unknown Type"}
                </span>
              </span>
            }
            options={workplaceRequestTypes.map((type) => ({
              id: type.id,
              name: type.name,
              icon: parseIconValue(type.icon) ?? null,
              tileColor:
                categoryColorById.get(type.category_id) ||
                DEFAULT_CATEGORY_COLOR
            }))}
          />
        }
      />

      {/* Schedule Info */}
      {schedule && (
        <>
          <DetailsSideItem
            label={
              <div className="flex items-center justify-between gap-2">
                <span className={classes.label}>Schedule</span>
                {canEdit ? (
                  <Button
                    variant="ghost"
                    size="sm"
                    iconLeft={<Pencil className="h-3.5 w-3.5" />}
                    onClick={() => setIsEditingSchedule(true)}
                    // DetailsSideItem wraps the whole label slot in
                    // classes.label, whose uppercase transform is inherited
                    // by every descendant — override it back for the button.
                    className="h-6 px-1.5 text-neutral-500 normal-case hover:text-neutral-900"
                  >
                    Edit
                  </Button>
                ) : null}
              </div>
            }
            value={
              <ScheduleInfoCard
                schedule={schedule}
                className="!m-0 !p-0"
                innerClassName="!m-0 !cursor-pointer hover:!border-neutral-300 transition-colors"
              />
            }
          />
          <SafeReserveEditModal
            editModal={isEditingSchedule}
            toggleModal={() => setIsEditingSchedule(false)}
            schedule={schedule}
            canRespondCancel={false}
            respondCancel={() => {}}
          />
        </>
      )}

      {/* Parent request — only on a request a workflow spawned */}
      {request.parent && onOpenLinkedRequest ? (
        <ParentRequestCard
          request={request.parent}
          typeName={parentType?.name ?? null}
          typeIcon={parseIconValue(parentType?.icon) ?? null}
          typeColor={
            categoryColorById.get(parentType?.category_id ?? "") ||
            DEFAULT_CATEGORY_COLOR
          }
          onOpen={() => {
            if (request.parent) onOpenLinkedRequest(request.parent);
          }}
        />
      ) : null}

      {/* Activity */}
      <div className="border-neutral-200 border-t pt-5">
        <div className={classes.label}>Activity</div>
        <div className="space-y-2">
          <ActivityRow label="Created" value={request.created} />
          <ActivityRow label="Last updated" value={request.updated} />
        </div>
      </div>
    </div>
  );
};

const ActivityRow = ({ label, value }: { label: string; value?: string }) => (
  <div className="flex items-center justify-between font-inter text-sm">
    <span className="text-neutral-500">{label}</span>
    <span className="text-neutral-900">
      {value ? formatDistanceToNow(new Date(value), { addSuffix: true }) : "—"}
    </span>
  </div>
);
